import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import PublicIcon from '@mui/icons-material/Public';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import EngineeringIcon from '@mui/icons-material/Engineering';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { useConfig } from '../../../utils/useConfig';

function About() {
  const config = useConfig();
  const mainColor = config.theme.colors.primary;

  // Animacje z framer-motion
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  // Dane sekcji "Dlaczego my?"
  const features = [
    {
      id: 'experience',
      icon: <ElectricBoltIcon />,
      title: 'Doświadczenie',
      description: 'Wieloletnie doświadczenie w branży elektrycznej',
    },
    {
      id: 'quality',
      icon: <AssignmentTurnedInIcon />,
      title: 'Jakość',
      description: 'Produkty od sprawdzonych dostawców',
    },
    {
      id: 'support',
      icon: <EngineeringIcon />,
      title: 'Wsparcie',
      description: 'Profesjonalne doradztwo techniczne',
    },
    {
      id: 'delivery',
      icon: <LocalShippingIcon />,
      title: 'Logistyka',
      description: 'Szybka dostawa na terenie całego kraju',
    },
  ];

  // Dane mediów społecznościowych z konfiguracją ikon
  const socialMediaConfig = [
    {
      name: 'Facebook',
      icon: <FacebookIcon className="text-lg sm:text-xl" />,
      url: config.social.facebook,
      color: '#1877F2',
      hoverBg: '#1877F220',
    },
    {
      name: 'Instagram',
      icon: <InstagramIcon className="text-lg sm:text-xl" />,
      url: config.social.instagram,
      color: '#E4405F',
      hoverBg: '#E4405F20',
    },
    {
      name: 'YouTube',
      icon: <YouTubeIcon className="text-lg sm:text-xl" />,
      url: config.social.youtube,
      color: '#FF0000',
      hoverBg: '#FF000020',
    },
    {
      name: 'Twitter',
      icon: <TwitterIcon className="text-lg sm:text-xl" />,
      url: config.social.twitter,
      color: '#1DA1F2',
      hoverBg: '#1DA1F220',
    },
    {
      name: 'LinkedIn',
      icon: <LinkedInIcon className="text-lg sm:text-xl" />,
      url: config.social.linkedin,
      color: '#0A66C2',
      hoverBg: '#0A66C220',
    },
    {
      name: 'Google',
      icon: <PublicIcon className="text-lg sm:text-xl" />,
      url: config.social.google,
      color: '#4285F4',
      hoverBg: '#4285F420',
    },
  ];

  // Filtrowanie, żeby pokazać tylko dostępne media społecznościowe
  const availableSocialMedia = socialMediaConfig.filter(
    (platform) => platform.url,
  );

  // Sprawdzenie czy są jakiekolwiek media społecznościowe do wyświetlenia
  const hasSocialMedia = availableSocialMedia.length > 0;

  return (
    <div
      className="w-full py-12 md:py-16 px-4"
      style={{
        background: `linear-gradient(to bottom, white, ${mainColor}08)`,
      }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Nagłówek sekcji */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariants}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-black to-primary">
              O firmie {config.company.name}
            </span>
          </h2>
          <div className="mx-auto w-16 md:w-20 h-1.5 rounded-full mb-3 md:mb-4 bg-primary"></div>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto">
            Poznaj naszą historię, wartości i to, co nas wyróżnia na rynku
          </p>
        </motion.div>

        {/* Sekcja główna */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="mb-12 md:mb-16"
        >
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-8 items-start">
            {/* Lewa kolumna - informacje o firmie */}
            <motion.div
              variants={itemVariants}
              className="col-span-1 md:col-span-3 bg-white rounded-xl overflow-hidden shadow-md"
            >
              <div className="relative h-48 md:h-64 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/60 z-10"></div>
                <img
                  src={
                    config.company.logo ||
                    'https://via.placeholder.com/800x400?text=Firma+Image'
                  }
                  alt={config.company.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 p-6 z-20">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className="h-12 w-12 rounded-full bg-white/90 flex items-center justify-center p-2">
                      <img
                        src={config.company.logo}
                        alt={config.company.name}
                        className="max-h-full max-w-full object-contain"
                      />
                    </div>
                    <h3 className="text-2xl font-bold text-white">
                      {config.company.name}
                    </h3>
                  </div>
                  <p className="text-white/90 text-sm">
                    {config.company.slogan || 'Jakość i profesjonalizm od lat'}
                  </p>
                </div>
              </div>

              <div className="p-6">
                <div className="prose prose-sm md:prose-base max-w-none text-gray-700">
                  <p className="leading-relaxed">
                    {config.company.about.shortInfo}
                  </p>
                </div>

                <motion.div
                  whileHover={{
                    y: -2,
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                  }}
                  className="mt-6 flex"
                >
                  <Link
                    to="/o-nas"
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-300 text-white bg-primary"
                  >
                    <span>Poznaj nas lepiej</span>
                    <KeyboardArrowRightIcon fontSize="small" />
                  </Link>
                </motion.div>
              </div>
            </motion.div>

            {/* Prawa kolumna - dlaczego my */}
            <motion.div
              variants={itemVariants}
              className="col-span-1 md:col-span-2 bg-white rounded-xl shadow-md p-6"
            >
              <div className="flex items-center space-x-3 mb-5">
                <div className="p-3 rounded-full bg-primary/10">
                  <BusinessIcon className="text-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">
                  Dlaczego my?
                </h3>
              </div>

              <div className="space-y-4">
                {features.map((feature) => (
                  <motion.div
                    key={feature.id}
                    whileHover={{
                      y: -2,
                      backgroundColor: 'rgba(var(--color-primary), 0.05)',
                      transition: { duration: 0.2 },
                    }}
                    className="p-4 rounded-lg border border-gray-100 transition-all duration-300"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="p-2 rounded-full bg-primary/10 text-primary">
                        {feature.icon}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {feature.title}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Sekcja mediów społecznościowych */}
        {hasSocialMedia && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="mb-8"
          >
            <motion.div variants={itemVariants} className="text-center mb-6">
              <h3 className="text-xl md:text-2xl font-bold mb-2">
                Śledź nas w mediach społecznościowych
              </h3>
              <p className="text-gray-600 text-sm md:text-base">
                Bądź na bieżąco z nowościami, promocjami i wydarzeniami
              </p>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4"
            >
              {availableSocialMedia.map((platform) => (
                <motion.a
                  key={platform.name}
                  href={platform.url!}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{
                    scale: 1.05,
                    backgroundColor: platform.hoverBg,
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                  }}
                  whileTap={{ scale: 0.95 }}
                  className="flex flex-col items-center justify-center p-4 rounded-xl bg-white shadow-sm border border-gray-100 transition-all duration-300"
                >
                  <div
                    className="h-12 w-12 rounded-full flex items-center justify-center mb-3"
                    style={{
                      backgroundColor: `${platform.color}20`,
                      color: platform.color,
                    }}
                  >
                    {platform.icon}
                  </div>
                  <span className="text-sm font-medium text-gray-800">
                    {platform.name}
                  </span>
                </motion.a>
              ))}
            </motion.div>
          </motion.div>
        )}

        {/* CTA section */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariants}
          className="mt-12 bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100"
        >
          <div className="md:flex items-stretch">
            <div className="relative md:w-2/5">
              <div
                className="absolute inset-0 z-10 md:z-0"
                style={{
                  background: `linear-gradient(to right, rgba(0,0,0,0.7), rgba(0,0,0,0.3))`,
                }}
              ></div>
              <img
                src={
                  config.company.logo ||
                  'https://via.placeholder.com/600x400?text=Contact+Us'
                }
                alt="Kontakt"
                className="w-full h-full object-cover min-h-[200px]"
              />
              <div className="absolute inset-0 flex items-center justify-center z-20 md:hidden">
                <h3 className="text-2xl text-white font-bold text-center px-4">
                  Potrzebujesz wsparcia?
                </h3>
              </div>
            </div>

            <div className="p-6 md:p-8 md:w-3/5">
              <div className="hidden md:block mb-4">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Potrzebujesz wsparcia?
                </h3>
                <div className="w-20 h-1 rounded bg-primary"></div>
              </div>

              <p className="text-gray-700 mb-6 max-w-xl">
                Nasz zespół specjalistów jest gotowy pomóc Ci w doborze
                odpowiednich produktów, udzielić porady technicznej lub
                odpowiedzieć na Twoje pytania.
              </p>

              <div className="flex flex-wrap gap-4">
                <motion.a
                  href={`tel:${config.contact.phone}`}
                  whileHover={{
                    y: -3,
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  }}
                  whileTap={{ scale: 0.97 }}
                  className="px-6 py-3 rounded-lg bg-white border-2 border-gray-200 text-gray-800 font-medium transition-all duration-300 flex items-center gap-2"
                >
                  <PhoneIcon fontSize="small" />
                  <span>{config.contact.phone}</span>
                </motion.a>

                <motion.a
                  href={`mailto:${config.contact.email}`}
                  whileHover={{
                    y: -3,
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                  }}
                  whileTap={{ scale: 0.97 }}
                  className="px-6 py-3 rounded-lg text-white font-medium transition-all duration-300 flex items-center gap-2 bg-primary"
                >
                  <EmailIcon fontSize="small" />
                  <span>Napisz do nas</span>
                </motion.a>

                <motion.a
                  href="/kontakt"
                  whileHover={{ y: -3 }}
                  whileTap={{ scale: 0.97 }}
                  className="px-6 py-3 rounded-lg bg-gray-800 text-white font-medium transition-all duration-300 flex items-center gap-2"
                >
                  <ContactIcon fontSize="small" />
                  <span>Pełne dane kontaktowe</span>
                </motion.a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// Import potrzebny do CTA
function ContactIcon(props: any) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      {...props}
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
      <circle cx="12" cy="10" r="3"></circle>
    </svg>
  );
}

export default About;
